import React, { memo, useCallback, useEffect, useMemo, forwardRef, useImperativeHandle, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import COMMON from "common";
import pathnames from "routes/pathnames";
import classNames from "common/class-names";
import sideNavList from "common/side-nav-list";
import AppLogoutIcon from "components/icons/app-logout-icon";

const AppSideNav = (props, ref) => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [activeIndex, setActiveIndex] = useState(0);
	const [isSidebarShrinked, setIsSidebarShrinked] = useState(false);
	const profile = useSelector((state) => state.profile);
	const logo = useMemo(() => profile.logo, [profile.logo]);
	const isApp = useMemo(() => sessionStorage.getItem(COMMON.EVENTS.APP), []);
	const company = useMemo(() => profile?.company || "", [profile]);
	const onHandleSetTitle = useMemo(() => props.onHandleSetTitle, [props.onHandleSetTitle]);
	const onHandleSetBackTitle = useMemo(() => props.onHandleSetBackTitle, [props.onHandleSetBackTitle]);

	const [expanded, setExpanded] = useState({});

	const enrichedSideNav = useMemo(() => {
		if (!profile?.company) return [];

		return sideNavList;
	}, [profile?.company]);

	//prettier-ignore
	const itemLabelClassName = useCallback((currentIndex) => {
		return classNames({ "side-nav__label": true, "side-nav__label--active": activeIndex === currentIndex });
	}, [activeIndex]);

	const itemChildLabelClassName = useCallback(
		(pathname, relatives) => {
			// prevent group items (like Team) from being active
			if (!pathname) {
				return classNames({ "side-nav__label": true });
			}

			const samePath = location.pathname === pathname;

			const relativesActive = relatives.findIndex((o) => location.pathname === o || location.pathname.startsWith(o + "/")) > -1;

			return classNames({
				"side-nav__label": true,
				"side-nav__label--child-active": samePath || relativesActive,
			});
		},
		[location]
	);

	//prettier-ignore
	const iconColor = useCallback((currentIndex) => {
		return currentIndex === activeIndex ? "#0245A9" : undefined;
	}, [activeIndex]);

	const onHandleLogout = useCallback(() => {
		localStorage.clear();
		sessionStorage.clear();
		dispatch({ type: COMMON.REDUX_ACTION.LOGOUT });
		navigate(pathnames.login);
	}, [dispatch, navigate]);

	const onHandleToggleSideNav = () => {
		const sideNav = document.getElementById("side-nav");
		const appAlert = document.getElementById("app-alert");
		const layoutMenu = document.getElementById("header-menu");
		const layoutContainer = document.getElementById("layout-container");

		if (appAlert) appAlert.classList.toggle("app-alert--shrinked");
		sideNav.classList.toggle("app-side-nav--shrinked");
		layoutMenu.classList.toggle("header__menu--shrinked");
		layoutContainer.classList.toggle("main__container--shrinked");

		setIsSidebarShrinked((prev) => !prev);
	};

	//prettier-ignore
	const onHandleHighlight = useCallback((index) => {
		setActiveIndex(index);

		setTimeout(() => {
			const items = document.querySelectorAll(".side-nav__item");
			const sideNavTarget = document.getElementById("side-nav-target");
			if (sideNavTarget && items[index]) return sideNavTarget.style.top = `${items[index]?.offsetTop}px`;
			else if (sideNavTarget) return sideNavTarget.style.top = `-300px`;
		});
	}, []);

	//prettier-ignore
	const onHandleItemChange = useCallback((obj, index) => {
		onHandleHighlight(index);
		if (!obj.childrens || obj.childrens.length === 0) {
			setExpanded({}); // collapse everything
		}

		if (!obj.childrens.length) {
			onHandleSetTitle(enrichedSideNav[index].label);
		}
		else {
			onHandleSetTitle(enrichedSideNav[index].childrens[0].label);
		}

		if (obj.childrens && obj.childrens.length > 0) {
			setExpanded(prev => {
				if (prev[obj.label]) {
					return {}; // collapse all
				}
				return { [obj.label]: true }; // open only this
			});

			// ALWAYS go to first child
			const firstChild = obj.childrens[0];

			if (firstChild?.path) {
				onHandleSetTitle(firstChild.label);
				navigate(firstChild.path);
			}

			return;
		}
		onHandleSetTitle(obj.label);
		navigate(obj.path);

	}, [navigate, enrichedSideNav, onHandleHighlight, onHandleSetTitle]);

	const SideNavChildrens = useCallback(
		(items, level = 1, parentLabel = null) => {
			if (!items || isSidebarShrinked) return null;

			return items.map((item) => {
				const hasChildren = item.childrens && item.childrens.length > 0;

				const handleClick = (event) => {
					event.stopPropagation();

					if (hasChildren) {
						setExpanded((prev) => {
							const newState = { ...prev };

							// collapse siblings (same level only)
							items.forEach((i) => {
								newState[i.label] = false;
							});

							//open current
							newState[item.label] = true;

							return newState;
						});

						//NEW: if no path, go to first child
						if (!item.path) {
							const firstChild = item.childrens?.[0];

							if (firstChild?.path) {
								onHandleSetTitle(firstChild.label);
								navigate(firstChild.path);
							}
						} else {
							// optional: if path exists, still allow navigation
							onHandleSetTitle(item.label);
							navigate(item.path);
						}
					} else {
						onHandleSetTitle(item.label);
						navigate(item.path);

						if (window.innerWidth <= 768) {
							onHandleToggleSideNav();
						}
					}
				};

				return (
					<div className="side-nav__children-group" key={item.label}>
						<div
							className="side-nav__children"
							style={{
								paddingLeft: `${level * 10}px`,
								marginTop: "23px",
							}}
							onClick={handleClick}
						>
							<p className={itemChildLabelClassName(item.path, item.relatives || [])}>{item.label}</p>
						</div>

						{hasChildren && expanded[item.label] && <div className="side-nav__children-nested">{SideNavChildrens(item.childrens, level + 1, item.label)}</div>}
					</div>
				);
			});
		},
		[location, isSidebarShrinked, expanded]
	);

	useEffect(() => {
		/* Set Nav Background To Specify Position */
		let sideNavIndex = null;

		const childrensIndex = enrichedSideNav.findIndex((parent) => {
			return parent.childrens?.some((child) => {
				// LEVEL 2
				if (location.pathname === child.path) {
					onHandleSetTitle(child.label);
					onHandleSetBackTitle("");
					return true;
				}

				//LEVEL 3
				const matchedGrand = child.childrens?.find((grand) => location.pathname === grand.path);

				if (matchedGrand) {
					onHandleSetTitle(matchedGrand.label); // USE GRAND LABEL
					onHandleSetBackTitle(child.label); // BACK TO PARENT
					return true;
				}

				// RELATIVES (safe)
				if (child.relatives?.some((r) => location.pathname === r || location.pathname.startsWith(r + "/"))) {
					onHandleSetTitle(child.label);
					onHandleSetBackTitle("");
					return true;
				}

				return false;
			});
		});

		if (childrensIndex > -1) {
			sideNavIndex = childrensIndex;
		} else {
			/* Find matching sideNav main path */
			const mainIndex = enrichedSideNav.findIndex((o) => o.path === location.pathname);

			if (mainIndex > -1 && childrensIndex === -1) {
				sideNavIndex = mainIndex;
				onHandleSetTitle(enrichedSideNav[mainIndex].label);
			}
		}

		if (id) onHandleSetTitle("");

		onHandleHighlight(sideNavIndex);
	}, [id, enrichedSideNav, onHandleSetTitle, location, onHandleHighlight, onHandleSetBackTitle]);

	useEffect(() => {
		if (!enrichedSideNav.length) return;

		const newExpanded = {};

		enrichedSideNav.forEach((parent) => {
			parent.childrens?.forEach((child) => {
				// Level 2 match
				if (location.pathname === child.path) {
					newExpanded[parent.label] = true;
				}

				// Level 3 match (Team)
				child.childrens?.forEach((grand) => {
					if (location.pathname === grand.path || location.pathname.startsWith(grand.path + "/")) {
						newExpanded[parent.label] = true;
						newExpanded[child.label] = true;
					}
				});
				if (child.relatives?.some((r) => location.pathname === r || location.pathname.startsWith(r + "/"))) {
					newExpanded[parent.label] = true;
					newExpanded[child.label] = true;
				}
			});
		});

		setExpanded(newExpanded);
	}, [location.pathname, enrichedSideNav]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleToggleSideNav: onHandleToggleSideNav,
	}));

	return (
		<div id="side-nav" className="app-side-nav">
			<div className="side-nav">
				<div className="side-nav__header">
					{!logo && company?.name && (
						<p className="side-nav__name" onClick={onHandleToggleSideNav}>
							{company.name}
						</p>
					)}
					{logo && <div className="side-nav__logo" onClick={onHandleToggleSideNav} style={{ backgroundImage: `url(${logo})` }} />}
				</div>
				<h1 className="side-nav__headline">HR Platform</h1>

				<nav className="side-nav__nav">
					<span id="side-nav-target" className="side-nav__background" />

					<ul className="side-nav__list">
						{enrichedSideNav.map((o, i) => {
							const Icon = o.icon;
							const activeItem = activeIndex === i;

							return (
								<li className="side-nav__item" key={i} onClick={() => onHandleItemChange(o, i)}>
									<div className="side-nav__wrapper">
										<div className="side-nav__content">
											<div className="side-nav__icon">
												<Icon color={iconColor(i)} />
											</div>
											<p className={itemLabelClassName(i)}>{o.label}</p>
										</div>
									</div>

									{(activeItem || expanded[o.label]) && o.childrens?.length > 0 && <div>{SideNavChildrens(o.childrens)}</div>}
								</li>
							);
						})}
					</ul>
					{!isApp && (
						<div className="side-nav__item side-nav__item--logout" onClick={onHandleLogout}>
							<div className="side-nav__wrapper">
								<div className="side-nav__icon">
									<AppLogoutIcon />
								</div>
								<p className="side-nav__label">Logout</p>
							</div>
						</div>
					)}
				</nav>
			</div>
		</div>
	);
};

export default memo(forwardRef(AppSideNav));

AppSideNav.propTypes = {
	onHandleSetTitle: PropTypes.func.isRequired,
	onHandleSetBackTitle: PropTypes.func.isRequired,
};
